import UTS from './trackers/UTS'
import { deepClone } from './utils'

export interface Tracker {
  load(): Promise<any>
  pageView(args): void
  event(args, hitCallback?): void
  push(data): void
  set(...args): void
}

export const TRACKER_READY_EVENT = 'trackerReady'

const TACKER_SETUP_READY = typeof window === 'undefined'
  ? Promise.resolve()
  : new Promise((resolve) =>
    window.addEventListener(TRACKER_READY_EVENT, resolve, { once: true })
  )

type Log = [number, ...any[]]

export default class TrackerImpl implements Tracker {
  _trackers: (Tracker)[] = []
  _trackingLogs: Log[] = []

  _debug = false

  constructor ({ debug, trackerConfigs, utsId }) {
    this._debug = debug

    const urlSearchParams = new URLSearchParams(window.location.search)
    const trackingId = urlSearchParams.get('tracking_id')
    trackerConfigs.trackingId = trackingId

    if (trackerConfigs?.uts?.context && utsId) {
      this._trackers.push(new UTS(trackerConfigs, utsId))
    }

    this.load()
  }

  load (): Promise<any> {
    return Promise.all(this._trackers.map(tracker => tracker.load()))
  }

  async pageView (args) {
    await this.load()
    await TACKER_SETUP_READY

    if (Array.isArray(args)) {
      args = {
        event: args[0],
        options: args[1],
        callback: args[2],
        config: args[3]
      }
    }

    const { event, options, config } = args
    const { category, params, screenName, title } = event || {}

    this.log('pageView', category, params, screenName, title, options, config)
    this._trackers.forEach((tracker) => tracker.pageView(args))
  }

  async event (args, hitCallback?) {
    await this.load()
    await TACKER_SETUP_READY

    if (Array.isArray(args)) {
      args = {
        event: args[0],
        options: args[1],
        callback: args[2],
        config: args[3]
      }
    }

    const { event, options, config } = args
    const { category, params } = event || {}
    if (!category) {
      console.error('Tracking data are not enough.')
      return
    }

    this.log('event', category, params, options, config)

    // TODO: consider how to use hitCallback in here

    this._trackers.forEach((tracker) => tracker.event(args, hitCallback))
  }

  async push (data) {
    await this.load()
    await TACKER_SETUP_READY

    this.log('push', data)
    this._trackers.forEach((tracker) => tracker.push(data))
  }

  async set (key, ...values) {
    await this.load()

    this.log('set', key, ...values)
    this._trackers.forEach((tracker) => tracker.set(key, ...values))
  }

  log (...args) {
    if (!this._debug) {
      return
    }

    this._trackingLogs.splice(99)
    // Remove trailing empty fields.
    while (args.length && args[args.length - 1] === undefined) {
      args.pop()
    }
    this._trackingLogs.unshift([Date.now(), ...args.map(deepClone)])
  }

  get logs () {
    return this._trackingLogs
  }
}
